/**
 * ChartHistoryDrawer
 *
 * An inline collapsible panel that sits between the chart controls and the chart
 * image, letting the user browse and load historical charts via cascading filters.
 *
 * Filter flow:
 *   1. Chart Type  → Performance | Exposure
 *   2. Year        → derived from available history
 *   3. Period       → Weekly | Monthly
 *   4. Month        → (only when Period = Weekly) derived from available data
 *   → Results: clickable entries; selecting one loads that chart and closes the drawer
 *
 * Props:
 *   open          {boolean}   Whether the panel is expanded
 *   onClose       {Function}  Called when user dismisses / selects a chart
 *   chartHistory  {Object}    { week: { performance: [], exposure: [] }, month: { ... } }
 *   onSelectChart {Function}  (url: string, label: string) => void
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';

import THEME from '../../constants/theme';
import { monthName } from '../../services/cmsService';

// ─── helpers ─────────────────────────────────────────────────────────────────

const TOGGLE_SX = {
  '& .MuiChip-root': {
    borderRadius: '6px',
    fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
    fontSize: '0.82rem',
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  }
};

const FilterRow = ({ label, children }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5,
      flexWrap: 'wrap'
    }}
  >
    <Typography
      sx={{
        fontSize: '0.78rem',
        fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
        color: THEME.COLORS.NEUTRAL_GRAY,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        minWidth: 72,
        mt: '3px',
        flexShrink: 0
      }}
    >
      {label}
    </Typography>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, ...TOGGLE_SX }}>
      {children}
    </Box>
  </Box>
);

const FilterChip = ({ label, selected, onClick }) => (
  <Chip
    label={label}
    size='small'
    onClick={onClick}
    sx={{
      backgroundColor: selected ? THEME.COLORS.PRIMARY_BLUE : '#e8f0ff',
      color: selected ? THEME.COLORS.NEUTRAL_WHITE : THEME.COLORS.PRIMARY_BLUE,
      borderColor: selected ? THEME.COLORS.PRIMARY_BLUE : 'transparent',
      border: '1px solid',
      boxShadow: selected ? '0 2px 8px rgba(0,0,0,0.18)' : 'none',
      px: 1,
      py: 0.8,
      height: 'auto',
      '& .MuiChip-label': {
        px: 0.5
      },
      '&:hover': {
        backgroundColor: selected
          ? THEME.COLORS.PRIMARY_BLUE
          : THEME.COLORS.PRIMARY_BLUE + '28'
      },
      '&:focus': { outline: '2px solid ' + THEME.COLORS.PRIMARY_BLUE }
    }}
  />
);

const ResultChip = ({ label, selected, onClick }) => (
  <Chip
    label={label}
    size='small'
    onClick={onClick}
    variant={selected ? 'filled' : 'outlined'}
    sx={{
      borderRadius: '6px',
      borderColor: THEME.COLORS.PRIMARY_BLUE,
      color: selected ? THEME.COLORS.NEUTRAL_WHITE : THEME.COLORS.PRIMARY_BLUE,
      backgroundColor: selected ? THEME.COLORS.PRIMARY_BLUE : 'transparent',
      fontSize: '0.82rem',
      fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
      cursor: 'pointer',
      px: 1,
      py: 0.8,
      height: 'auto',
      boxShadow: selected ? '0 2px 8px rgba(0,0,0,0.18)' : 'none',
      '& .MuiChip-label': {
        px: 0.5
      },
      '&:hover': {
        backgroundColor: selected
          ? THEME.COLORS.PRIMARY_BLUE
          : THEME.COLORS.PRIMARY_BLUE + '14',
        borderColor: THEME.COLORS.PRIMARY_BLUE
      }
    }}
  />
);

// ─── main component ───────────────────────────────────────────────────────────

const ChartHistoryDrawer = ({ open, onClose, chartHistory, onSelectChart }) => {
  const [selType, setSelType] = useState('performance');
  const [selYear, setSelYear] = useState(null);
  const [selPeriod, setSelPeriod] = useState(null);
  const [selMonth, setSelMonth] = useState(null);
  const [selEntryKey, setSelEntryKey] = useState(null);

  // Keep selection state when closing/reopening the drawer.

  const getEntryKey = entry =>
    `${selType}-${selPeriod}-${entry.year}-${entry.week ?? entry.month}`;

  // All entries for the selected type across both periods
  const weekEntries = chartHistory?.week?.[selType] ?? [];
  const monthEntries = chartHistory?.month?.[selType] ?? [];

  // Available years across both periods for the selected type
  const allYears = [
    ...new Set([
      ...weekEntries.map(e => e.year),
      ...monthEntries.map(e => e.year)
    ])
  ].sort((a, b) => b - a);

  // When year changes, reset downstream selections
  const handleYearSelect = year => {
    setSelYear(year);
    setSelPeriod(null);
    setSelMonth(null);
    setSelEntryKey(null);
  };

  // When period changes, reset month
  const handlePeriodSelect = period => {
    setSelPeriod(period);
    setSelMonth(null);
    setSelEntryKey(null);
  };

  // Available months for weekly entries in the selected year
  const availableMonths =
    selYear && selPeriod === 'week'
      ? [
          ...new Set(
            weekEntries.filter(e => e.year === selYear).map(e => e.month)
          )
        ]
          .filter(Boolean)
          .sort((a, b) => b - a)
      : [];

  // Whether period buttons are available for the selected year
  const hasWeekly = selYear
    ? weekEntries.some(e => e.year === selYear)
    : weekEntries.length > 0;
  const hasMonthly = selYear
    ? monthEntries.some(e => e.year === selYear)
    : monthEntries.length > 0;

  // Final filtered results
  const results = (() => {
    if (!selYear || !selPeriod) return [];

    if (selPeriod === 'month') {
      return monthEntries.filter(e => e.year === selYear);
    }

    if (selPeriod === 'week') {
      const filtered = weekEntries.filter(e => e.year === selYear);
      if (selMonth) {
        return filtered.filter(e => e.month === selMonth);
      }
      // Show all if no months exist (entries without month metadata)
      return availableMonths.length === 0 ? filtered : [];
    }

    return [];
  })();

  const handleSelectEntry = entry => {
    setSelEntryKey(getEntryKey(entry));
    onSelectChart(entry.url, entry.label);
  };

  return (
    <Collapse in={open} timeout={220} unmountOnExit>
      <Box
        role='region'
        aria-label='Chart history'
        sx={{
          mb: 2,
          borderRadius: '8px',
          backgroundColor: '#f0f4ff',
          border: `1px solid ${THEME.COLORS.PRIMARY_BLUE}28`,
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 1.5,
            backgroundColor: THEME.COLORS.PRIMARY_BLUE,
            borderRadius: '7px 7px 0 0'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon
              sx={{ fontSize: '1.1rem', color: THEME.COLORS.NEUTRAL_WHITE }}
            />
            <Typography
              sx={{
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                fontSize: '0.9rem',
                color: THEME.COLORS.NEUTRAL_WHITE,
                letterSpacing: '0.3px'
              }}
            >
              Chart History
            </Typography>
          </Box>
          <Tooltip title='Close history'>
            <IconButton
              size='small'
              onClick={onClose}
              aria-label='Close chart history'
              sx={{
                color: THEME.COLORS.NEUTRAL_WHITE,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' }
              }}
            >
              <CloseIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Filters */}
        <Box
          sx={{
            px: 2.5,
            py: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.6
          }}
        >
          {/* Step 1: Chart Type */}
          <FilterRow label='Type'>
            {['performance', 'exposure'].map(t => (
              <FilterChip
                key={t}
                label={t.charAt(0).toUpperCase() + t.slice(1)}
                selected={selType === t}
                onClick={() => {
                  setSelType(t);
                  setSelYear(null);
                  setSelPeriod(null);
                  setSelMonth(null);
                  setSelEntryKey(null);
                }}
              />
            ))}
          </FilterRow>

          {/* Step 2: Year */}
          {allYears.length > 0 && (
            <FilterRow label='Year'>
              {allYears.map(year => (
                <FilterChip
                  key={year}
                  label={String(year)}
                  selected={selYear === year}
                  onClick={() => handleYearSelect(year)}
                />
              ))}
            </FilterRow>
          )}

          {/* Step 3: Period (shown after year is selected) */}
          {selYear && (hasWeekly || hasMonthly) && (
            <FilterRow label='Period'>
              {hasWeekly && (
                <FilterChip
                  label='Weekly'
                  selected={selPeriod === 'week'}
                  onClick={() => handlePeriodSelect('week')}
                />
              )}
              {hasMonthly && (
                <FilterChip
                  label='Monthly'
                  selected={selPeriod === 'month'}
                  onClick={() => handlePeriodSelect('month')}
                />
              )}
            </FilterRow>
          )}

          {/* Step 4: Month (only for weekly) */}
          {selPeriod === 'week' && availableMonths.length > 0 && (
            <FilterRow label='Month'>
              {availableMonths.map(m => (
                <FilterChip
                  key={m}
                  label={monthName(m)}
                  selected={selMonth === m}
                  onClick={() => {
                    setSelMonth(m === selMonth ? null : m);
                    setSelEntryKey(null);
                  }}
                />
              ))}
            </FilterRow>
          )}

          {/* Results */}
          {results.length > 0 && (
            <>
              <Divider
                sx={{ borderColor: THEME.COLORS.PRIMARY_BLUE + '20', my: 0.5 }}
              />
              <FilterRow label='Select'>
                {results.map((entry, i) => (
                  <ResultChip
                    key={`${entry.year}-${entry.week ?? entry.month}-${i}`}
                    label={entry.label}
                    selected={selEntryKey === getEntryKey(entry)}
                    onClick={() => handleSelectEntry(entry)}
                  />
                ))}
              </FilterRow>
            </>
          )}

          {/* Empty state */}
          {selYear && selPeriod && results.length === 0 && (
            <Typography
              sx={{
                fontSize: '0.82rem',
                color: THEME.COLORS.NEUTRAL_GRAY,
                fontStyle: 'italic',
                pl: '76px'
              }}
            >
              {selPeriod === 'week' && availableMonths.length > 0 && !selMonth
                ? 'Select a month to see available weeks.'
                : 'No charts available for this selection.'}
            </Typography>
          )}
        </Box>
      </Box>
    </Collapse>
  );
};

export default ChartHistoryDrawer;
